import * as React from 'react';
import styled, { ThemeContext } from 'styled-components';
import { Checkbox as CheckboxBase } from '../checkbox';
import { ICheckboxGroupProps, ICheckboxGroupOption, IkeyType } from './checkbox-group-types';
import { excludeKeys as excludeKeysBase, memoizeFunction } from '@agilepoint/utils-common';
import { StackPanel } from '../stack-panel';
import { ITheme, selectThemeSemantics } from '../../styling';
import { Typography } from '../typography';
import { CheckboxGroupContent } from './checkbox-group-content';

interface ICheckBoxGroupState {
    checkedKeys: React.Key[];
}

const excludeKeys = memoizeFunction(excludeKeysBase);
const SELECT_ALL_KEYS = '__SELECT__ALL__';

const GroupLabel = styled.legend<Pick<ICheckboxGroupProps, 'required'>>`
    padding: 0px 0px 10px 0px;
    &:after {
        content: ${({ required }) => (required ? '\'*\'' : '')};;
        color: ${selectThemeSemantics(({ palette }) => palette.red)};
    }
`;

export const StyledCheckbox = styled(CheckboxBase) <{ $orientation?: string, $maxItemWidth?: number, $minItemWidth?: number }>`
    display: ${({ $orientation }) => ($orientation === 'vertical' ? 'block' : 'inline-block')};
    max-width: ${({ $maxItemWidth }) => ($maxItemWidth ? `${$maxItemWidth}px` : '')};
    min-width: ${({ $minItemWidth }) => ($minItemWidth ? `${$minItemWidth}px` : '')};
`;

class CheckboxGroupBase extends React.PureComponent<ICheckboxGroupProps, ICheckBoxGroupState> {
    static contextType = ThemeContext;
    declare context: ITheme;
    static getDerivedStateFromProps(props: ICheckboxGroupProps) {
        const { checkedKeys, maximumSelection } = props;
        if (checkedKeys !== undefined) {
            return {
                checkedKeys: checkedKeys.slice(0, maximumSelection)
            };
        }
        return null;
    }

    constructor(props: ICheckboxGroupProps) {
        super(props);
        const { checkedKeys, defaultCheckedKeys = [] } = this.props;
        const newCheckedKeys = checkedKeys || defaultCheckedKeys;
        this.state = {
            checkedKeys: this.getCheckedKeys(newCheckedKeys)
        };
    }

    componentDidUpdate(prevProps: ICheckboxGroupProps) {
        const { maximumSelection } = this.props;
        const { checkedKeys } = this.state;

        if (maximumSelection !== undefined &&
            prevProps.maximumSelection !== maximumSelection &&
            checkedKeys.length > maximumSelection) {

            this.setState({
                checkedKeys: this.getCheckedKeys(checkedKeys)
            });
        }
    }

    render() {
        const { options, className, required, groupLabel, hidden } = this.props;
        const { checkbox: { composites: { groupCheckboxGap } } } = this.context;
        return hidden ? null : (
            <div className={className}>
                {groupLabel && <GroupLabel className="ap-checkboxGroup-label" required={required}><Typography.H5>{groupLabel}</Typography.H5></GroupLabel>}
                <StackPanel className="ap-checkboxGroup-options" wrap orientation={this.getOrientation()} gap={String(groupCheckboxGap)}>
                    <>
                        {this.renderSelectAllCheckbox()}
                        {options.map(this.renderCheckboxGroupContent)}
                    </>
                </StackPanel>
            </div>
        );
    }

    private renderCheckboxGroupContent = (item: ICheckboxGroupOption) => {
        const { checkedKeys } = this.state;

        const checkboxGroupProps = excludeKeys(this.props, 'checkedKeys', 'defaultCheckedKeys', 'maximumSelection',
            'options', 'selectAll', 'selectAllLabel', 'onValueChange', 'className', 'groupLabel');

        const isChecked = checkedKeys.includes(item.key);

        return (
            <CheckboxGroupContent
                {...checkboxGroupProps}
                key={item.key}
                itemData={item}
                isChecked={isChecked}
                onChangeHandler={this._onChange}
                disabled={this.isDisabled(item.key)} />
        );
    }

    private renderSelectAllCheckbox() {
        const { selectAll, selectAllLabel = 'Select All', disabled, options, minItemWidth, maxItemWidth, maximumSelection, onRenderItem } = this.props;
        const { checkedKeys } = this.state;

        if (!selectAll) {
            return null;
        }

        const checkboxState = options.slice(0, maximumSelection).length === this.getCheckedKeys(checkedKeys).length ? true : 'intermediate';
        const isChecked = checkedKeys.length ? checkboxState : false;

        if (onRenderItem) {
            return onRenderItem({
                isSelected: isChecked,
                isDisabled: maximumSelection === 0 ? true : (!!disabled),
                itemData: { key: SELECT_ALL_KEYS, name: selectAllLabel, value: SELECT_ALL_KEYS },
                toggleSelection: this.onCustomSelectAllChange
            });
        }

        return (
            <StyledCheckbox label={selectAllLabel}
                name={selectAllLabel}
                checked={isChecked}
                $minItemWidth={minItemWidth}
                $maxItemWidth={maxItemWidth}
                onValueChange={this._onSelectAllChange}
                disabled={maximumSelection === 0 ? true : disabled} />
        );
    }

    private onCustomSelectAllChange = () => {
        const { checkedKeys } = this.state;
        const { maximumSelection } = this.props;
        const { options } = this.props;
        const selectAllStatus = options.slice(0, maximumSelection).length === this.getCheckedKeys(checkedKeys).length;
        this._onSelectAllChange(!selectAllStatus);
    }

    private getOrientation() {
        const { orientation = 'horizontal' } = this.props;
        return orientation;
    }

    private getCheckedKeys(checkedKeys: React.Key[]) {
        const { maximumSelection } = this.props;
        return checkedKeys.slice(0, maximumSelection);
    }

    private isDisabled = (itemKey: React.Key) => {
        const { disabled, maximumSelection } = this.props;
        const { checkedKeys } = this.state;

        return disabled || (checkedKeys.length === maximumSelection && !checkedKeys.includes(itemKey));
    }

    private _onSelectAllChange = (checked: boolean) => {
        const { options, maximumSelection } = this.props;
        const checkedOptions = checked ? options.slice(0, maximumSelection) : [];
        const checkedKeys = checkedOptions.map((item) => item.key);

        this.updateCheckedKeys(checkedKeys, checkedOptions);
    }

    private _onChange = (checked: boolean, key: IkeyType) => {
        const { options } = this.props;
        const { checkedKeys: checkedKeysInState } = this.state;
        let checkedKeys = checkedKeysInState.slice();
        if (checked) {
            checkedKeys.push(key as React.Key);
        } else {
            checkedKeys = checkedKeys.filter(checkedKey => checkedKey !== key as React.Key);
        }

        const checkedOptions = options.filter(checkedKey => checkedKeys.includes(checkedKey.key));

        this.updateCheckedKeys(checkedKeys, checkedOptions);
    }

    private updateCheckedKeys(checkedKeys: React.Key[], checkedOptions: ICheckboxGroupOption[]) {
        const { onValueChange } = this.props;

        this.setState({
            checkedKeys
        });

        if (onValueChange) {
            onValueChange(checkedKeys, checkedOptions);
        }
    }
}

export const CheckboxGroup = styled(CheckboxGroupBase as React.ComponentType<ICheckboxGroupProps>).attrs({
    className: String('ap-checkboxGroup')
})`
    display: inline-block;
`;
